/**
 * Executive Inbox Page — Brief C
 *
 * Surface where the Evaluator (executive tier) sees all Proposals currently
 * awaiting their review and takes Catchball resolution actions.
 *
 * Route: /prioritization/cascade/executive-inbox
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Inbox,
  CheckCircle2,
  AlertCircle,
  RotateCcw,
  XCircle,
  Clock,
  User,
  FileText,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Loader2,
  AlertTriangle,
  List,
  MessageSquare,
  History,
  Shield,
  ArrowUpRight,
} from 'lucide-react';
import { useCatchballStore, type LoopResult } from '../../../stores/prioritization/catchballStore';
import { useCycleReportStore, type CycleReport } from '../../../stores/prioritization/cycleReportStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { getSafeSupabaseClient, getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { CATCHBALL_LABELS } from '../../../lib/catchball/vocabulary';
import { useCatchballAcceptRight } from '../../../hooks/prioritization/useActRight';
import { refusedByRight } from '../../../lib/actRight';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import {
  PorChangeRequestBadge,
  PorChangeRequestBanner,
} from '../../../components/prioritization/catchball/PorChangeRequestBanner';
import {
  getPorChangeRequestContext,
  previewPorAmendmentDiff,
  type PorChangeRequestContext,
} from '../../../lib/porChangeRequest';
import { LoopDiffBlock } from '../../../components/prioritization/catchball/LoopDiffBlock';
import { AllocationIfAccepted } from '../../../components/prioritization/allocation/AllocationIfAccepted';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface InboxProposal {
  id: string;
  loopId: string | null;
  cycleId: string;
  cycleName: string;
  trackId: string | null;
  trackName: string | null;
  loopNumber: number | null;
  iterationNumber: number | null;
  loopState: string;
  proposedBy: string;
  proposedByName: string | null;
  proposedAt: string;
  isOverdue: boolean;
  itemCount: number;
  rationale: string;
  tradeoffs: unknown[] | null;
  disagreements: unknown[] | null;
  assumptions: string | null;
  aggregatedRankList: string[];
  targetRecipients: string[];
  /** Owner of the proposal's cycle — may resolve even when not a designated recipient. */
  cycleOwnerId: string | null;
  parentProposalId: string | null;
  /** Session that produced the ranking (for provenance) */
  finalSessionReportId: string | null;
  finalSessionCreatedAt: string | null;
}

interface ItemTitle {
  id: string;
  title: string;
}

type Resolution = 'accept' | 'accept_conditions' | 'return' | 'reject';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function daysSince(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
}

// ─── Status badge ─────────────────────────────────────────────────────────────

function OverdueBadge({ days }: { days: number }) {
  if (days < 3) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[var(--coral-dim)] px-2 py-0.5 text-xs font-medium text-[var(--coral)]">
      <AlertTriangle className="h-3 w-3" />
      {days}d waiting
    </span>
  );
}

// ─── Inbox card ───────────────────────────────────────────────────────────────

function InboxCard({
  proposal,
  onSelect,
  changeRequestContext,
}: {
  proposal: InboxProposal;
  onSelect: () => void;
  changeRequestContext?: PorChangeRequestContext;
}) {
  const days = daysSince(proposal.proposedAt);
  const isChangeRequest = changeRequestContext?.isChangeRequest === true;
  return (
    <button
      onClick={onSelect}
      data-proposal-id={proposal.id}
      aria-label={`Review proposal from ${
        proposal.proposedByName ?? proposal.proposedBy
      } — ${proposal.cycleName}${proposal.trackName ? ` · ${proposal.trackName}` : ''} · ${
        proposal.itemCount
      } item${proposal.itemCount !== 1 ? 's' : ''} · proposed ${new Date(
        proposal.proposedAt,
      ).toLocaleDateString()}`}
      className="w-full text-left border border-[var(--rule)] bg-[var(--bg-raised)] p-4 hover:border-primary-300 hover:shadow-sm transition-all group"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)]">
              {proposal.cycleName}
            </span>
            {proposal.loopNumber != null && (
              <span className="text-xs text-[var(--text-ter)]">· Loop #{proposal.loopNumber}</span>
            )}
            {(proposal.iterationNumber ?? 1) > 1 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[var(--mustard-dim)] px-2 py-0.5 text-xs font-medium text-[var(--mustard)]">
                <History className="h-3 w-3" />
                Revision {proposal.iterationNumber}
              </span>
            )}
            {isChangeRequest && changeRequestContext.currentVersion != null && (
              <PorChangeRequestBadge currentVersion={changeRequestContext.currentVersion} />
            )}
          </div>
          {proposal.trackName && (
            <p className="mt-0.5 text-sm font-medium text-[var(--text-pri)] truncate">{proposal.trackName}</p>
          )}
          <div className="mt-1.5 flex items-center gap-3 text-xs text-[var(--text-ter)]">
            <span className="flex items-center gap-1">
              <User className="h-3 w-3" />
              {proposal.proposedByName ?? proposal.proposedBy.slice(0, 8) + '…'}
            </span>
            <span className="flex items-center gap-1">
              <List className="h-3 w-3" />
              {proposal.itemCount} item{proposal.itemCount !== 1 ? 's' : ''}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {new Date(proposal.proposedAt).toLocaleDateString()}
            </span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          <OverdueBadge days={days} />
          <span
            data-proposal-id={proposal.id}
            data-testid="inbox-review-cta"
            className="inline-flex items-center gap-1 bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-700 group-hover:bg-primary-100 transition-colors"
          >
            Review
            <ChevronRight className="h-3 w-3" />
          </span>
        </div>
      </div>
    </button>
  );
}

// ─── Confirmation dialog ──────────────────────────────────────────────────────

function ConfirmDialog({
  resolution,
  onConfirm,
  onCancel,
  isSubmitting,
  changeRequestContext,
  cycleId,
  proposalId,
}: {
  resolution: Resolution;
  onConfirm: () => void;
  onCancel: () => void;
  isSubmitting: boolean;
  changeRequestContext?: PorChangeRequestContext;
  cycleId?: string;
  proposalId: string;
}) {
  const isChangeRequest =
    resolution === 'accept' && changeRequestContext?.isChangeRequest === true;
  const messages: Record<Resolution, string> = {
    accept: isChangeRequest
      ? `Accepting this change request writes Priorities v${changeRequestContext!.nextVersion} and supersedes the current cycle report (v${changeRequestContext!.currentVersion}). This cannot be undone without a new governance action.`
      : 'Accepting this proposal ratifies the priorities. This cannot be undone without a new governance action.',
    accept_conditions: 'Accepting with conditions sends the proposal to provisional status. The worker must resolve conditions before full ratification.',
    return: 'Returning this proposal sends it back for revision. The worker will see your guidance and resubmit.',
    reject: 'Rejecting this proposal closes the loop without ratification. This cannot be undone without a new governance action.',
  };

  const labels: Record<Resolution, { button: string; color: string }> = {
    accept: {
      button: isChangeRequest ? 'Confirm Change Request' : `Confirm ${CATCHBALL_LABELS.acceptAndRatify}`,
      color: 'bg-[var(--green)] hover:bg-[var(--green)]',
    },
    accept_conditions: { button: `Confirm ${CATCHBALL_LABELS.acceptWithConditions}`, color: 'bg-[var(--mustard)] hover:bg-[var(--mustard)]' },
    return: { button: `Confirm ${CATCHBALL_LABELS.returnForRevision}`, color: 'bg-[var(--mustard)] hover:bg-[var(--mustard)]' },
    reject: { button: `Confirm ${CATCHBALL_LABELS.reject}`, color: 'bg-[var(--coral)] hover:bg-[var(--coral)]' },
  };

  return (
    <div className="border border-[var(--mustard)] bg-[var(--mustard-dim)] p-4 space-y-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
        <p className="text-sm text-[var(--mustard)]">{messages[resolution]}</p>
      </div>
      {/* Cell Allocation: how the shares would stand (C-2, migration 972) */}
      {resolution === 'accept' && cycleId && (
        <AllocationIfAccepted cycleId={cycleId} proposalId={proposalId} />
      )}
      <div className="flex gap-3">
        <button
          onClick={onConfirm}
          disabled={isSubmitting}
          className={`inline-flex items-center gap-2  px-4 py-2 text-sm font-medium text-white disabled:opacity-50 transition-colors ${labels[resolution].color}`}
        >
          {isSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {labels[resolution].button}
        </button>
        <button
          onClick={onCancel}
          disabled={isSubmitting}
          className="inline-flex items-center gap-2 border border-[var(--rule)] px-4 py-2 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Action panel ─────────────────────────────────────────────────────────────

function DecisionPanel({
  proposal,
  isExecutive,
  onResolved,
  changeRequestContext,
}: {
  proposal: InboxProposal;
  isExecutive: boolean;
  onResolved: (action: Resolution) => void;
  changeRequestContext?: PorChangeRequestContext;
}) {
  const userId = useUserStore((s) => s.userId);
  const workspaceId = getCurrentWorkspaceId() ?? '';
  const acceptProposal = useCatchballStore((s) => s.acceptProposal);
  const acceptProposalWithConditions = useCatchballStore((s) => s.acceptProposalWithConditions);
  const returnProposal = useCatchballStore((s) => s.returnProposal);
  const rejectProposal = useCatchballStore((s) => s.rejectProposal);

  const [acceptNote, setAcceptNote] = useState('');
  const [acceptAuthority, setAcceptAuthority] = useState('');
  const [conditionsText, setConditionsText] = useState('');
  const [revisionGuidance, setRevisionGuidance] = useState('');
  const [rejectRationale, setRejectRationale] = useState('');
  const [pendingAction, setPendingAction] = useState<Resolution | null>(null);
  const [confirmAction, setConfirmAction] = useState<Resolution | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // D-4 (migration 987): who may accept is the database's answer — the rule
  // catchball_accept acts on, including the cycle's "Items / selection"
  // decision right when one is set. Accept is drawn only for a person who may
  // use it; otherwise a plain line says what it takes and who holds it (§2.8).
  const acceptRight = useCatchballAcceptRight(isExecutive ? proposal.id : null);

  const validate = (action: Resolution): string | null => {
    if (action === 'accept_conditions' && !conditionsText.trim())
      return `Conditions are required for ${CATCHBALL_LABELS.acceptWithConditions}.`;
    if (action === 'return' && !revisionGuidance.trim())
      return `Revision guidance is required for ${CATCHBALL_LABELS.returnForRevision}.`;
    if (action === 'reject' && !rejectRationale.trim())
      return 'Rejection rationale is required.';
    return null;
  };

  const handleAction = (action: Resolution) => {
    setError(null);
    const validationError = validate(action);
    if (validationError) {
      setError(validationError);
      return;
    }
    setConfirmAction(action);
  };

  const handleConfirm = async () => {
    if (!confirmAction || !userId) return;
    // Empty-string loopId reaches the RPC as an invalid uuid ('' → 22P02) and
    // the action fails opaquely. Surface the real problem instead.
    if (!proposal.loopId) {
      setError('This proposal has no Catchball loop attached — it cannot be resolved from the inbox.');
      setConfirmAction(null);
      return;
    }
    setIsSubmitting(true);
    setError(null);

    try {
      let success = false;
      const base = {
        proposalId: proposal.id,
        loopId: proposal.loopId,
        cycleId: proposal.cycleId,
        actorId: userId,
        workspaceId,
      };

      if (confirmAction === 'accept') {
        success = await acceptProposal({
          ...base,
          rationale: acceptNote || undefined,
          ratificationAuthority: acceptAuthority.trim() || undefined,
        });
      } else if (confirmAction === 'accept_conditions') {
        success = await acceptProposalWithConditions({ ...base, conditions: conditionsText });
      } else if (confirmAction === 'return') {
        success = await returnProposal({ ...base, revisionGuidance });
      } else if (confirmAction === 'reject') {
        success = await rejectProposal({ ...base, rationale: rejectRationale });
      }

      if (success) {
        onResolved(confirmAction);
      } else {
        // The store actions return false instead of throwing; the server's
        // reason is kept on the store's `error` field.
        setError(
          useCatchballStore.getState().error
            || 'The action did not go through and nothing was recorded. Try again.',
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unexpected error.');
    } finally {
      setIsSubmitting(false);
      setConfirmAction(null);
    }
  };

  if (!isExecutive) {
    return (
      <div className="border border-[var(--rule)] bg-[var(--surface)] p-4 flex items-start gap-3">
        <Shield className="h-5 w-5 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-[var(--text-sec)]">Read-only view</p>
          <p className="text-xs text-[var(--text-ter)] mt-0.5">
            Only designated reviewers can take resolution actions on this proposal.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-start gap-2 border border-[var(--coral)] bg-[var(--coral-dim)] p-3">
          <AlertCircle className="h-4 w-4 text-[var(--coral)] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[var(--coral)]">{error}</p>
        </div>
      )}

      {confirmAction ? (
        <ConfirmDialog
          resolution={confirmAction}
          onConfirm={handleConfirm}
          onCancel={() => setConfirmAction(null)}
          isSubmitting={isSubmitting}
          changeRequestContext={changeRequestContext}
          cycleId={proposal.cycleId}
          proposalId={proposal.id}
        />
      ) : (
        <div className="grid gap-3">
          {acceptRight.line && (
            <p className="border border-[var(--rule)] bg-[var(--surface)] px-4 py-3 text-sm text-[var(--text-sec)]">
              {acceptRight.line}
            </p>
          )}

          {/* Accept */}
          {acceptRight.allowed && (
            <div className="border border-[var(--rule)] bg-[var(--bg-raised)] overflow-hidden">
              <button
                onClick={() => setPendingAction(pendingAction === 'accept' ? null : 'accept')}
                className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-[var(--text-pri)] hover:bg-[var(--surface)] transition-colors"
              >
                <div className="flex items-start gap-2 text-left">
                  <CheckCircle2 className="h-4 w-4 text-[var(--green)] mt-0.5 flex-shrink-0" />
                  <div>
                    <div>
                      {changeRequestContext?.isChangeRequest
                        ? 'Accept Change Request'
                        : CATCHBALL_LABELS.acceptAndRatify}
                    </div>
                    <div className="text-xs font-normal text-[var(--green)] dark:text-[var(--text-ter)] mt-0.5">
                      {changeRequestContext?.isChangeRequest
                        ? `Writes Priorities v${changeRequestContext.nextVersion} and supersedes the current cycle report.`
                        : 'Ranking becomes a governed priority commitment. Creates a governance record.'}
                    </div>
                  </div>
                </div>
                <span className="text-xs text-[var(--text-ter)] flex-shrink-0 ml-3">Optional note</span>
              </button>
              {pendingAction === 'accept' && (
                <div className="border-t border-[var(--rule)] px-4 pb-4 pt-3 space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-[var(--text-ter)] mb-1">
                      Acting authority <span className="font-normal text-[var(--text-ter)]">(recorded in the cycle report)</span>
                    </label>
                    <input
                      type="text"
                      value={acceptAuthority}
                      onChange={(e) => setAcceptAuthority(e.target.value)}
                      placeholder="e.g. CEO, VP Product, Head of Engineering…"
                      className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-neutral-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                  <textarea
                    value={acceptNote}
                    onChange={(e) => setAcceptNote(e.target.value)}
                    placeholder="Optional note for the submitter…"
                    rows={2}
                    className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-neutral-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 resize-none"
                  />
                  <button
                    onClick={() => handleAction('accept')}
                    className="inline-flex items-center gap-2 bg-[var(--green)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--green)] transition-colors"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {changeRequestContext?.isChangeRequest
                      ? 'Accept Change Request'
                      : CATCHBALL_LABELS.acceptAndRatify}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Accept with conditions */}
          {acceptRight.allowedProvisional && (
            <div className="border border-[var(--rule)] bg-[var(--bg-raised)] overflow-hidden">
              <button
                onClick={() => setPendingAction(pendingAction === 'accept_conditions' ? null : 'accept_conditions')}
                className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-[var(--text-pri)] hover:bg-[var(--surface)] transition-colors"
              >
                <div className="flex items-start gap-2 text-left">
                  <CheckCircle2 className="h-4 w-4 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
                  <div>
                    <div>{CATCHBALL_LABELS.acceptWithConditions}</div>
                    <div className="text-xs font-normal text-[var(--mustard)] mt-0.5">Provisional priority commitment. Conditions must be resolved before full ratification.</div>
                  </div>
                </div>
                <span className="text-xs text-[var(--coral)] font-normal flex-shrink-0 ml-3">Conditions required</span>
              </button>
              {pendingAction === 'accept_conditions' && (
                <div className="border-t border-[var(--rule)] px-4 pb-4 pt-3 space-y-2">
                  <textarea
                    value={conditionsText}
                    onChange={(e) => setConditionsText(e.target.value)}
                    placeholder="What must be resolved before full acceptance? Be specific…"
                    rows={3}
                    className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-neutral-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 resize-none"
                  />
                  {/* Named inline error — always visible when field is empty */}
                  {!conditionsText.trim() && (
                    <p className="text-xs text-[var(--coral)] font-medium">
                      Conditions must be specified before conditional acceptance
                    </p>
                  )}
                  <button
                    onClick={() => handleAction('accept_conditions')}
                    disabled={!conditionsText.trim()}
                    className="inline-flex items-center gap-2 bg-[var(--mustard)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--mustard)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {CATCHBALL_LABELS.acceptWithConditions}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* 1001: rejecting and returning exercise the right accepting does — not drawn when it shuts this person out (build contract §2.8) */}
          {!refusedByRight(acceptRight) && (<>
          {/* Return for revision */}
          <div className="border border-[var(--rule)] bg-[var(--bg-raised)] overflow-hidden">
            <button
              onClick={() => setPendingAction(pendingAction === 'return' ? null : 'return')}
              className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-[var(--text-pri)] hover:bg-[var(--surface)] transition-colors"
            >
              <div className="flex items-start gap-2 text-left">
                <RotateCcw className="h-4 w-4 text-[var(--text-ter)] mt-0.5 flex-shrink-0" />
                <div>
                  <div>{CATCHBALL_LABELS.returnForRevision}</div>
                  <div className="text-xs font-normal text-[var(--text-ter)] mt-0.5">Sends the proposal back with required changes. No priority is committed yet.</div>
                </div>
              </div>
              <span className="text-xs text-[var(--coral)] font-normal flex-shrink-0 ml-3">Guidance required</span>
            </button>
            {pendingAction === 'return' && (
              <div className="border-t border-[var(--rule)] px-4 pb-4 pt-3 space-y-2">
                <textarea
                  value={revisionGuidance}
                  onChange={(e) => setRevisionGuidance(e.target.value)}
                  placeholder="What should the submitter revise? Be clear and actionable…"
                  rows={3}
                  className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-neutral-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 resize-none"
                />
                {/* Named inline error — always visible when field is empty */}
                {!revisionGuidance.trim() && (
                  <p className="text-xs text-[var(--coral)] font-medium">
                    Revision guidance is required before returning
                  </p>
                )}
                <button
                  onClick={() => handleAction('return')}
                  disabled={!revisionGuidance.trim()}
                  className="inline-flex items-center gap-2 bg-[var(--slate)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  {CATCHBALL_LABELS.returnForRevision}
                </button>
              </div>
            )}
          </div>

          {/* Reject */}
          <div className="border border-[var(--rule)] bg-[var(--bg-raised)] overflow-hidden">
            <button
              onClick={() => setPendingAction(pendingAction === 'reject' ? null : 'reject')}
              className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-[var(--text-pri)] hover:bg-[var(--surface)] transition-colors"
            >
              <div className="flex items-start gap-2 text-left">
                <XCircle className="h-4 w-4 text-[var(--coral)] mt-0.5 flex-shrink-0" />
                <div>
                  <div>{CATCHBALL_LABELS.reject}</div>
                  <div className="text-xs font-normal text-[var(--coral)] mt-0.5">Closes this loop without committing any priority. Creates a governance record of rejection.</div>
                </div>
              </div>
              <span className="text-xs text-[var(--coral)] font-normal flex-shrink-0 ml-3">Rationale required</span>
            </button>
            {pendingAction === 'reject' && (
              <div className="border-t border-[var(--rule)] px-4 pb-4 pt-3 space-y-2">
                <textarea
                  value={rejectRationale}
                  onChange={(e) => setRejectRationale(e.target.value)}
                  placeholder="Why is this proposal being rejected? Rationale is required…"
                  rows={3}
                  className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-neutral-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 resize-none"
                />
                {/* Named inline error — always visible when field is empty */}
                {!rejectRationale.trim() && (
                  <p className="text-xs text-[var(--coral)] font-medium">
                    Rejection rationale is required
                  </p>
                )}
                <button
                  onClick={() => handleAction('reject')}
                  disabled={!rejectRationale.trim()}
                  className="inline-flex items-center gap-2 bg-[var(--coral)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--coral)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <XCircle className="h-3.5 w-3.5" />
                  {CATCHBALL_LABELS.reject}
                </button>
              </div>
            )}
          </div>
          </>)}
        </div>
      )}
    </div>
  );
}

// ─── Loop History Section (Brief D) ──────────────────────────────────────────

const DECISION_LABELS: Record<string, { label: string; color: string }> = {
  accepted: { label: 'Accepted', color: 'text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)]' },
  accepted_provisional: { label: 'Accepted (Provisional)', color: 'text-[var(--mustard)] bg-[var(--mustard-dim)]' },
  returned: { label: 'Returned', color: 'text-[var(--text-sec)] bg-[var(--slate-dim)]' },
  rejected: { label: 'Rejected', color: 'text-[var(--coral)] bg-[var(--coral-dim)]' },
};

function LoopHistorySection({ loopId }: { loopId: string }) {
  const fetchLoopHistory = useCatchballStore((s) => s.fetchLoopHistory);
  const [history, setHistory] = useState<LoopResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedTurns, setExpandedTurns] = useState<Set<string>>(new Set());

  useEffect(() => {
    setIsLoading(true);
    fetchLoopHistory(loopId)
      .then((rows) => setHistory(rows))
      .finally(() => setIsLoading(false));
  }, [loopId, fetchLoopHistory]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-[var(--text-ter)] py-1">
        <Loader2 className="h-3 w-3 animate-spin" />
        Loading history…
      </div>
    );
  }

  // Show section only when there's more than one turn
  if (history.length <= 1) return null;

  const toggleTurn = (id: string) => {
    setExpandedTurns((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <section>
      <div className="mb-3 flex items-center gap-2">
        <History className="h-4 w-4 text-[var(--text-ter)]" />
        <h3 className="text-sm font-semibold text-[var(--text-pri)]">Prior Turns</h3>
        <span className="rounded-full bg-[var(--hover-tint)] px-2 py-0.5 text-xs font-medium text-[var(--text-ter)]">
          {history.length - 1} previous turn{history.length - 1 !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="border border-[var(--rule)] overflow-hidden divide-y divide-[var(--rule)] bg-[var(--bg-raised)]">
        {history.slice(0, -1).map((turn) => {
          const isExpanded = expandedTurns.has(turn.id);
          const decision = turn.evaluator_decision
            ? DECISION_LABELS[turn.evaluator_decision] ?? { label: turn.evaluator_decision, color: 'text-[var(--text-sec)] bg-[var(--hover-tint)]' }
            : null;
          return (
            <div key={turn.id}>
              <button
                onClick={() => toggleTurn(turn.id)}
                className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-[var(--surface)] transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-xs font-semibold text-[var(--text-ter)] flex-shrink-0">
                    Turn #{turn.turn_number}
                  </span>
                  <span className="text-xs text-[var(--text-ter)] flex-shrink-0">
                    {new Date(turn.submitted_at).toLocaleDateString()}
                  </span>
                  {decision && (
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${decision.color}`}>
                      {decision.label}
                    </span>
                  )}
                  {!decision && (
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-[var(--hover-tint)] text-[var(--text-ter)]">
                      Pending decision
                    </span>
                  )}
                </div>
                {isExpanded
                  ? <ChevronUp className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
                  : <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
                }
              </button>
              {isExpanded && (
                <div className="border-t border-[var(--rule)] px-4 pb-4 pt-3 space-y-2 bg-[var(--surface)]">
                  {turn.rationale && (
                    <div>
                      <p className="text-xs font-medium text-[var(--text-ter)] mb-0.5">Rationale</p>
                      <p className="text-sm text-[var(--text-sec)] whitespace-pre-wrap leading-relaxed">{turn.rationale}</p>
                    </div>
                  )}
                  {turn.evaluator_rationale && (
                    <div>
                      <p className="text-xs font-medium text-[var(--text-ter)] mb-0.5">Evaluator Response</p>
                      <p className="text-sm text-[var(--text-sec)] whitespace-pre-wrap leading-relaxed">{turn.evaluator_rationale}</p>
                    </div>
                  )}
                  {turn.revision_guidance && (
                    <div>
                      <p className="text-xs font-medium text-[var(--text-ter)] mb-0.5">Revision Guidance</p>
                      <p className="text-sm text-[var(--text-sec)] whitespace-pre-wrap leading-relaxed">{turn.revision_guidance}</p>
                    </div>
                  )}
                  {turn.resolved_at && (
                    <p className="text-xs text-[var(--text-ter)]">
                      Resolved {new Date(turn.resolved_at).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ─── Proposal Detail View ─────────────────────────────────────────────────────

function ProposalDetailView({
  proposal,
  itemTitles,
  isExecutive,
  onBack,
  onResolved,
  currentReport,
}: {
  proposal: InboxProposal;
  itemTitles: Record<string, string>;
  isExecutive: boolean;
  onBack: () => void;
  onResolved: (action: Resolution) => void;
  currentReport?: CycleReport | null;
}) {
  const days = daysSince(proposal.proposedAt);
  const changeRequestContext = getPorChangeRequestContext(currentReport);
  const previewDiff = changeRequestContext.isChangeRequest
    ? previewPorAmendmentDiff(
        proposal.aggregatedRankList,
        currentReport?.planOfRecord,
        itemTitles,
      )
    : [];

  return (
    <div className="space-y-6">
      {/* Back + header */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 border border-[var(--rule)] px-3 py-1.5 text-sm text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Inbox
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-[var(--text-ter)]">
            {proposal.cycleName}
            {proposal.loopNumber != null && ` · Loop #${proposal.loopNumber}`}
          </p>
          {proposal.trackName && (
            <h2 className="text-base font-semibold text-[var(--text-pri)] truncate">{proposal.trackName}</h2>
          )}
        </div>
        {days >= 3 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-[var(--coral-dim)] px-2.5 py-1 text-xs font-medium text-[var(--coral)]">
            <AlertTriangle className="h-3 w-3" />
            {days}d waiting
          </span>
        )}
      </div>

      {changeRequestContext.isChangeRequest && (
        <PorChangeRequestBanner
          cycleId={proposal.cycleId}
          context={changeRequestContext}
          previewDiff={previewDiff}
        />
      )}

      {/* What changed since the last pass of this ball (roadmap C-1). Hidden
          when there is nothing earlier to compare with. */}
      {proposal.loopId && (
        <LoopDiffBlock loopId={proposal.loopId} proposalId={proposal.id} variant="inbox" />
      )}

      {/* Section 1 — The Ranking */}
      <section>
        <div className="mb-3 flex items-center gap-2">
          <List className="h-4 w-4 text-[var(--text-ter)]" />
          <h3 className="text-sm font-semibold text-[var(--text-pri)]">The Ranking</h3>
          {(proposal.iterationNumber ?? 1) > 1 && (
            <span className="rounded-full bg-[var(--mustard-dim)] px-2 py-0.5 text-xs font-medium text-[var(--mustard)]">
              Revision {proposal.iterationNumber}
            </span>
          )}
        </div>
        <div className="border border-[var(--rule)] overflow-hidden bg-[var(--bg-raised)]">
          <table className="w-full">
            <thead className="bg-[var(--surface)] border-b border-[var(--rule)]">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-[var(--text-ter)] w-10">Rank</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-[var(--text-ter)]">Item</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--rule)]">
              {proposal.aggregatedRankList.map((itemId, index) => (
                <tr key={itemId} className="hover:bg-[var(--surface)]">
                  <td className="px-4 py-2.5 text-xs font-bold text-[var(--text-ter)]">#{index + 1}</td>
                  <td className="px-4 py-2.5 text-sm text-[var(--text-pri)]">
                    {itemTitles[itemId] ?? itemId.slice(0, 8) + '…'}
                  </td>
                </tr>
              ))}
              {proposal.aggregatedRankList.length === 0 && (
                <tr>
                  <td colSpan={2} className="px-4 py-4 text-sm text-[var(--text-ter)] text-center">
                    No items in ranking
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Section 2 — The Rationale */}
      <section>
        <div className="mb-3 flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-[var(--text-ter)]" />
          <h3 className="text-sm font-semibold text-[var(--text-pri)]">The Rationale</h3>
        </div>
        <div className="border border-[var(--rule)] bg-[var(--bg-raised)] divide-y divide-[var(--rule)] overflow-hidden">
          <div className="px-4 py-3">
            <p className="text-xs font-medium text-[var(--text-ter)] mb-1">Rationale</p>
            <p className="text-sm text-[var(--text-pri)] whitespace-pre-wrap leading-relaxed">
              {proposal.rationale || <span className="italic text-[var(--text-ter)]">Not provided</span>}
            </p>
          </div>
          {proposal.tradeoffs && Array.isArray(proposal.tradeoffs) && proposal.tradeoffs.length > 0 && (
            <div className="px-4 py-3">
              <p className="text-xs font-medium text-[var(--text-ter)] mb-1">Tradeoffs</p>
              <ul className="space-y-1">
                {(proposal.tradeoffs as Array<{ what?: string; why?: string }>).map((t, i) => (
                  <li key={i} className="text-sm text-[var(--text-sec)]">
                    {t.what}{t.why ? ` — ${t.why}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {proposal.disagreements && Array.isArray(proposal.disagreements) && proposal.disagreements.length > 0 && (
            <div className="px-4 py-3">
              <p className="text-xs font-medium text-[var(--text-ter)] mb-1">Noted Disagreements</p>
              <ul className="space-y-1">
                {(proposal.disagreements as Array<{ item?: string; reason?: string }>).map((d, i) => (
                  <li key={i} className="text-sm text-[var(--text-sec)]">
                    {d.item}{d.reason ? ` — ${d.reason}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {proposal.assumptions && (
            <div className="px-4 py-3">
              <p className="text-xs font-medium text-[var(--text-ter)] mb-1">Assumptions</p>
              <p className="text-sm text-[var(--text-sec)] whitespace-pre-wrap">{proposal.assumptions}</p>
            </div>
          )}
        </div>
      </section>

      {/* Section 3 — Prior Turns (Brief D — only when turn_number > 1) */}
      {(proposal.iterationNumber ?? 1) > 1 && proposal.loopId && (
        <LoopHistorySection loopId={proposal.loopId} />
      )}

      {/* Section 4 — Context (full provenance per correction §3) */}
      <section>
        <div className="mb-3 flex items-center gap-2">
          <FileText className="h-4 w-4 text-[var(--text-ter)]" />
          <h3 className="text-sm font-semibold text-[var(--text-pri)]">Context</h3>
        </div>
        <div className="border border-[var(--rule)] bg-[var(--bg-raised)] divide-y divide-[var(--rule)] overflow-hidden">
          {/* Submission provenance block */}
          <div className="px-4 py-3 bg-[var(--surface)]">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1.5">Submission provenance</p>
            <p className="text-sm text-[var(--text-pri)]">
              Submitted by{' '}
              <span className="font-medium">{proposal.proposedByName ?? proposal.proposedBy}</span>
              {' '}on{' '}
              <span className="font-medium">{new Date(proposal.proposedAt).toLocaleString()}</span>
            </p>
            {proposal.finalSessionReportId && (
              <p className="text-xs text-[var(--text-ter)] mt-1">
                Based on session:{' '}
                <span className="font-mono bg-[var(--hover-tint)] rounded px-1">{proposal.finalSessionReportId.slice(0, 8)}…</span>
                {' '}· {proposal.itemCount} item{proposal.itemCount !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          {/* Loop & cycle location */}
          <ContextRow label="Cycle" value={proposal.cycleName} />
          <ContextRow label="Track" value={proposal.trackName ?? '—'} />
          <ContextRow
            label="Loop"
            value={proposal.loopNumber != null ? `Loop #${proposal.loopNumber}` : '—'}
          />
          <ContextRow
            label="Turn"
            value={
              (proposal.iterationNumber ?? 1) === 1
                ? 'Turn 1 — First submission'
                : `Turn ${proposal.iterationNumber} — Revision`
            }
          />

          {/* Prior turn chain */}
          {proposal.parentProposalId && (
            <div className="px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1">Prior turn</p>
              <p className="text-sm text-[var(--text-sec)]">
                This is a revision of an earlier proposal
                {' '}(<span className="font-mono text-xs bg-[var(--hover-tint)] rounded px-1">{proposal.parentProposalId.slice(0, 8)}…</span>).
              </p>
              <p className="text-xs text-[var(--text-ter)] mt-0.5">
                The original proposal was returned by an executive. The worker has addressed the revision guidance and resubmitted.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Section 5 — Your Decision */}
      <section>
        <div className="mb-3 flex items-center gap-2">
          <Shield className="h-4 w-4 text-[var(--text-ter)]" />
          <h3 className="text-sm font-semibold text-[var(--text-pri)]">Your Decision</h3>
        </div>
        <DecisionPanel
          proposal={proposal}
          isExecutive={isExecutive}
          onResolved={onResolved}
          changeRequestContext={changeRequestContext}
        />
      </section>
    </div>
  );
}

function ContextRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5">
      <span className="text-xs font-medium text-[var(--text-ter)]">{label}</span>
      <span className="text-sm text-[var(--text-pri)] text-right max-w-[60%] truncate">{value}</span>
    </div>
  );
}

// ─── Resolved Success Banner ──────────────────────────────────────────────────

function ResolvedBanner({
  action,
  onDismiss,
  cycleId,
  wasChangeRequest,
}: {
  action: Resolution;
  onDismiss: () => void;
  cycleId?: string | null;
  wasChangeRequest?: boolean;
}) {
  const labels: Record<Resolution, { icon: React.ReactNode; text: string; color: string }> = {
    accept: {
      icon: <CheckCircle2 className="h-5 w-5 text-[var(--green)]" />,
      text: wasChangeRequest
        ? 'Change request accepted — new cycle report version written.'
        : 'Proposal accepted — priorities ratified.',
      color: 'border-[var(--green)] bg-[var(--green-dim)]',
    },
    accept_conditions: {
      icon: <CheckCircle2 className="h-5 w-5 text-[var(--mustard)]" />,
      text: 'Proposal accepted with conditions — ball returned to worker.',
      color: 'border-[var(--mustard)] bg-[var(--mustard-dim)]',
    },
    return: {
      icon: <RotateCcw className="h-5 w-5 text-[var(--text-ter)]" />,
      text: 'Proposal returned for revision — worker has been notified.',
      color: 'border-[var(--slate)] bg-[var(--slate-dim)]',
    },
    reject: {
      icon: <XCircle className="h-5 w-5 text-[var(--coral)]" />,
      text: 'Proposal rejected — loop closed without ratification.',
      color: 'border-[var(--coral)] bg-[var(--coral-dim)]',
    },
  };

  const config = labels[action];

  return (
    <div className={`flex items-start gap-3  border p-4 ${config.color}`}>
      {config.icon}
      <div className="flex-1">
        <p className="text-sm font-medium text-[var(--text-pri)]">{config.text}</p>
        {action === 'accept' && cycleId && (
          <Link
            to={`/prioritization/cycles/${cycleId}/report`}
            className="inline-flex items-center gap-1 mt-1.5 text-sm text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)] font-medium underline"
          >
            Priorities Ratified · View Report
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        )}
      </div>
      <button
        onClick={onDismiss}
        className="text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
      >
        ×
      </button>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ExecutiveInboxPage() {
  const userId = useUserStore((s) => s.userId);
  const navigate = useNavigate();

  const [proposals, setProposals] = useState<InboxProposal[]>([]);
  const [itemTitles, setItemTitles] = useState<Record<string, string>>({});
  const [cycleReportsByCycleId, setCycleReportsByCycleId] = useState<Record<string, CycleReport | null>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProposal, setSelectedProposal] = useState<InboxProposal | null>(null);
  const [resolvedAction, setResolvedAction] = useState<Resolution | null>(null);
  const [resolvedProposalId, setResolvedProposalId] = useState<string | null>(null);
  const [resolvedCycleId, setResolvedCycleId] = useState<string | null>(null);
  const [resolvedWasChangeRequest, setResolvedWasChangeRequest] = useState(false);

  const fetchCurrentCycleReport = useCycleReportStore((s) => s.fetchCurrentCycleReport);

  const fetchInbox = useCallback(async () => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId() ?? '';
    if (!supabase || !userId) {
      setIsLoading(false);
      return;
    }
    void workspaceId; // available for future use

    setIsLoading(true);
    setError(null);

    try {
      // Fetch proposals with status=proposed that are addressed to current user or in cycles they own
      const { data, error: fetchError } = await supabase
        .from('catchball_proposals')
        .select(`
          id,
          cycle_id,
          loop_id,
          iteration_number,
          proposed_by,
          proposed_by_name,
          proposed_at,
          target_recipients,
          rationale,
          supporting_evidence,
          parent_proposal_id,
          resolution_note,
          aggregated_rank_list,
          final_s6_session_report_id,
          session_report_id,
          degap_cycles!cycle_id (
            name,
            owner_id
          ),
          catchball_loops!loop_id (
            loop_number,
            state,
            track_id,
            tracks!track_id (
              name
            )
          )
        `)
        .eq('status', 'proposed')
        .order('proposed_at', { ascending: true });

      if (fetchError) {
        setError(fetchError.message);
        return;
      }

      if (!data) {
        setProposals([]);
        return;
      }

      // Client-side filter: must be in target_recipients OR cycle owner
      const filtered = data.filter((row: Record<string, unknown>) => {
        const recipients = (row.target_recipients as string[]) ?? [];
        const cycle = row.degap_cycles as Record<string, unknown> | null;
        return recipients.includes(userId) || cycle?.owner_id === userId;
      });


      const mapped: InboxProposal[] = filtered.map((row: Record<string, unknown>) => {
        const cycle = row.degap_cycles as Record<string, unknown>;
        // Loop join (F9): without loop number and track the rows in a busy inbox
        // are indistinguishable until opened.
        const loop = row.catchball_loops as Record<string, unknown> | null;
        const loopTrack = loop?.tracks as Record<string, unknown> | null;
        const days = daysSince(row.proposed_at as string);
        const rankList = (row.aggregated_rank_list as string[] | null) ?? [];
        return {
          id: row.id as string,
          loopId: (row.loop_id as string | null) ?? null,
          cycleId: row.cycle_id as string,
          cycleName: (cycle?.name as string) ?? 'Unknown Cycle',
          trackId: (loop?.track_id as string | null) ?? null,
          trackName: (loopTrack?.name as string | null) ?? null,
          loopNumber: (loop?.loop_number as number | null) ?? null,
          iterationNumber: (row.iteration_number as number | null) ?? null,
          loopState: (loop?.state as string | null) ?? 'proposed',
          proposedBy: row.proposed_by as string,
          proposedByName: row.proposed_by_name as string | null,
          proposedAt: row.proposed_at as string,
          isOverdue: days >= 3,
          itemCount: rankList.length,
          rationale: (row.rationale as string) ?? '',
          tradeoffs: null,
          disagreements: null,
          assumptions: null,
          aggregatedRankList: rankList,
          targetRecipients: (row.target_recipients as string[]) ?? [],
          cycleOwnerId: (cycle?.owner_id as string | null) ?? null,
          parentProposalId: row.parent_proposal_id as string | null,
          finalSessionReportId:
            (row.final_s6_session_report_id as string | null) ??
            (row.session_report_id as string | null),
          finalSessionCreatedAt: null,
        };
      });

      setProposals(mapped);

      // GAP-2: load current PoR per cycle so inbox can frame change requests
      const uniqueCycleIds = [...new Set(mapped.map((p) => p.cycleId).filter(Boolean))];
      const reportEntries = await Promise.all(
        uniqueCycleIds.map(async (cycleId) => {
          const report = await fetchCurrentCycleReport(cycleId);
          return [cycleId, report] as const;
        }),
      );
      const reportMap: Record<string, CycleReport | null> = {};
      for (const [cycleId, report] of reportEntries) {
        reportMap[cycleId] = report;
      }
      setCycleReportsByCycleId(reportMap);

      // Load item titles for all proposals
      const allItemIds = [...new Set(mapped.flatMap((p) => p.aggregatedRankList))];
      if (allItemIds.length > 0) {
        const { data: items } = await supabase
          .from('items')
          .select('id, title')
          .in('id', allItemIds);
        if (items) {
          const titleMap: Record<string, string> = {};
          for (const item of items as ItemTitle[]) {
            titleMap[item.id] = item.title;
          }
          setItemTitles(titleMap);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load inbox.');
    } finally {
      setIsLoading(false);
    }
  }, [userId, fetchCurrentCycleReport]);

  useEffect(() => {
    void fetchInbox();
  }, [fetchInbox]);

  const handleResolved = useCallback((action: Resolution) => {
    if (!selectedProposal) return;
    const wasChangeRequest = getPorChangeRequestContext(
      cycleReportsByCycleId[selectedProposal.cycleId],
    ).isChangeRequest;
    // Accept lands on the Plan of Record — never back on the inbox
    // (brief-X §1.5; Doc 26 rule 6). The cycle report is the artifact Accept produced.
    if (action === 'accept' && selectedProposal.cycleId) {
      navigate(`/prioritization/cycles/${selectedProposal.cycleId}/report`, {
        // loopId rides along so the landing can show what still stands between
        // ratification and the Plan of Record (F4 wayfinding).
        state: { ratifiedMoment: true, wasChangeRequest, loopId: selectedProposal.loopId },
      });
      return;
    }
    setResolvedAction(action);
    setResolvedProposalId(selectedProposal.id);
    setResolvedCycleId(selectedProposal.cycleId);
    setResolvedWasChangeRequest(wasChangeRequest);
    setSelectedProposal(null);
    // Remove from inbox list
    setProposals((prev) => prev.filter((p) => p.id !== selectedProposal.id));
  }, [selectedProposal, navigate, cycleReportsByCycleId]);

  // Mirror the server rule (catchball_accept / _reject / _return_for_revision,
  // 763 — internal_catchball_packet_authority): a designated recipient OR the
  // cycle owner may resolve (the server also admits the loop's requester,
  // workspace admins and catchball.* grants, which this inbox does not load). Previously this only checked recipients, so
  // proposals with an empty target_recipients array rendered read-only for the
  // very executive the RPC would have authorized.
  const isExecutiveFor = useCallback((proposal: InboxProposal) => {
    if (!userId) return false;
    return proposal.targetRecipients.includes(userId) || proposal.cycleOwnerId === userId;
  }, [userId]);

  // ─── Loading ────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-[var(--text-ter)]">
        <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
        <p className="text-sm">Loading inbox…</p>
      </div>
    );
  }

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Executive Inbox"
        subtitle="Proposals awaiting your review"
        icon={<Inbox className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        actions={
          <button
            onClick={() => void fetchInbox()}
            className="inline-flex items-center gap-2 border border-[var(--rule)] px-3 py-1.5 text-sm text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors"
          >
            Refresh
          </button>
        }
      />

      <div className="flex-1 overflow-y-auto px-6 py-6 max-w-3xl mx-auto w-full">
        {/* Error state */}
        {error && (
          <div className="mb-4 flex items-start gap-2 border border-[var(--coral)] bg-[var(--coral-dim)] p-4">
            <AlertCircle className="h-5 w-5 text-[var(--coral)] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-[var(--coral)]">Could not load inbox</p>
              <p className="text-sm text-[var(--coral)] mt-0.5">{error}</p>
            </div>
          </div>
        )}

        {/* Resolved banner */}
        {resolvedAction && resolvedProposalId && (
          <div className="mb-4">
            <ResolvedBanner
              action={resolvedAction}
              cycleId={resolvedCycleId}
              wasChangeRequest={resolvedWasChangeRequest}
              onDismiss={() => {
                setResolvedAction(null);
                setResolvedProposalId(null);
                setResolvedCycleId(null);
                setResolvedWasChangeRequest(false);
              }}
            />
          </div>
        )}

        {/* Proposal detail view */}
        {selectedProposal ? (
          <ProposalDetailView
            proposal={selectedProposal}
            itemTitles={itemTitles}
            isExecutive={isExecutiveFor(selectedProposal)}
            onBack={() => setSelectedProposal(null)}
            onResolved={handleResolved}
            currentReport={cycleReportsByCycleId[selectedProposal.cycleId] ?? null}
          />
        ) : (
          <>
            {/* Inbox list */}
            {proposals.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--hover-tint)] mb-4">
                  <Inbox className="h-7 w-7 text-[var(--text-ter)]" />
                </div>
                <p className="text-base font-medium text-[var(--text-sec)]">No proposals waiting for your review</p>
                <p className="text-sm text-[var(--text-ter)] mt-1 max-w-sm">
                  Proposals appear here when a working team submits their prioritization upward for executive review.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs font-medium uppercase tracking-wide text-[var(--text-ter)] mb-1">
                  {proposals.length} proposal{proposals.length !== 1 ? 's' : ''} awaiting review
                </p>
                {proposals.map((proposal) => (
                  <InboxCard
                    key={proposal.id}
                    proposal={proposal}
                    onSelect={() => setSelectedProposal(proposal)}
                    changeRequestContext={getPorChangeRequestContext(
                      cycleReportsByCycleId[proposal.cycleId],
                    )}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
